'use client';

import {
	BigNum,
	BN,
	PRICE_PRECISION_EXP,
	SpotMarketConfig,
	MarketType,
} from '@velocity-exchange/sdk';
import ValueDisplay from 'src/components/ValueDisplay';
import useAccountData from 'src/hooks/useAccountData';
import useMarkPrice from 'src/hooks/useMarkPrice';
import React, { useEffect, useState } from 'react';
import useUserAccountIsReady from '../../../hooks/useUserAccountIsReady';
import useDriftStore from '../../../stores/DriftStore/useDriftStore';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import { getSpotLiqPriceNum, getSpotLiqPriceStr } from 'src/utils/position';
import { toNotional } from 'src/utils/formatting';
import useDriftClientIsReady from '../../../hooks/useDriftClientIsReady';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useCalculateTradeFee from 'src/hooks/useCalculateTradeFee';
import {
	MarketId,
	UIOrderType,
	roundToStepSizeIfLargeEnough,
	TRADE_PRECISION,
	getMarketStepSizeDecimals,
	trimTrailingZeros,
} from '@velocity-exchange/common';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import usePreviewOrder from '../hooks/usePreviewOrder';
import LiquidationPriceDisplay from 'src/components/TradeForm/TradeDetails/LiquidationPriceDisplay';
import BottomDrawerSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/BottomDrawerSlippageToleranceInput';
import CollapsibleSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/CollapsibleSlippageToleranceInput';
import LimitOraclePriceDisplay from 'src/components/TradeForm/TradeDetails/LimitOraclePriceDisplay';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import useDriftClient from 'src/hooks/useDriftClient';

interface Props {
	orderType: UIOrderType;
	showSlippageTolerance?: boolean;
}

const SpotTradeDetails = ({ orderType, showSlippageTolerance }: Props) => {
	const markPrice = useMarkPrice();
	const [sizeInputSource] = useSizeInputSource();

	const isPostOnly = useDriftStore(
		(s) =>
			s.tradeForm.postOnly &&
			(s.tradeForm.orderType === 'limit' ||
				s.tradeForm.orderType === 'oracleLimit')
	);

	const tradeForm = useDriftStore((s) => s.tradeForm);

	const { baseSizeStringValue, quoteSizeStringValue } = tradeForm;
	const currentAccount = useAccountData();
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const side = useDriftStore((s) => s.tradeForm.side);
	const driftClientIsReady = useDriftClientIsReady();

	const currentPosition = useAccountTargetSpotBalance(
		selectedMarket?.market as SpotMarketConfig,
		currentAccount?.userKey ?? ''
	);

	const precision = (selectedMarket?.market as SpotMarketConfig).precisionExp;

	const previewOrderParams = usePreviewOrder();

	const { entryPrice, priceImpact } = previewOrderParams.priceImpact || {};

	const marketId = MarketId.createSpotMarket(selectedMarket?.marketIndex ?? 0);

	const stepSize = useMarketStepSize(marketId);

	const quoteSize = Number(quoteSizeStringValue);
	const baseSizeBigNum = BigNum.fromPrint(baseSizeStringValue, precision);

	const driftClient = useDriftClient();
	const stepSizeDecimalPlaces = driftClient
		? getMarketStepSizeDecimals(driftClient, marketId)
		: TRADE_PRECISION;

	const baseAssetSymbol = selectedMarket?.uiSymbols.baseAssetSymbol ?? '';

	const getPriceImpactDisplayValue = () => {
		const priceImpactNum = BigNum.from(
			priceImpact ? priceImpact : 0,
			PRICE_PRECISION_EXP
		).toNum();

		const priceImpactPct = priceImpactNum * 100;

		if (priceImpactPct < 0.01) {
			return `<0.01`;
		} else {
			return priceImpactPct.toFixed(2);
		}
	};

	const getEntryPrice = () =>
		BigNum.from(entryPrice ? entryPrice : 0, PRICE_PRECISION_EXP);

	const [_expectedPrice, setExpectedPrice] = useState(getEntryPrice());
	const [_estimatedPriceImpact, setEstimatedPriceImpact] = useState('0%');
	const [beforeLiqPriceNum, setBeforeLiqPriceNum] = useState(0);
	const [beforeLiqPriceStr, setBeforeLiqPriceStr] = useState('');
	const [afterLiqPriceNum, setAfterLiqPriceNum] = useState(0);
	const [afterLiqPriceStr, setAfterLiqPriceStr] = useState('');
	const userAccountIsReady = useUserAccountIsReady();
	const quoteSizeNum = Number(quoteSizeStringValue) || 0;

	const { tradeFee } = useCalculateTradeFee({
		quoteSize: quoteSizeNum,
		marketIndex: selectedMarket?.marketIndex ?? 0,
		marketType: MarketType.SPOT,
		isPostOnly: isPostOnly,
	});

	useEffect(() => {
		setEstimatedPriceImpact(`${getPriceImpactDisplayValue()}%`);
	}, [priceImpact]);

	useEffect(() => {
		setExpectedPrice(getEntryPrice());
	}, [entryPrice]);

	const previousBaseValue = currentPosition?.netBaseBalance ?? BigNum.zero();

	const afterBaseValue = previousBaseValue.add(
		BigNum.fromPrint(
			baseSizeStringValue ? baseSizeStringValue : '0',
			precision
		).mul(BigNum.from(side === 'buy' ? new BN(1) : new BN(-1)))
	);

	useEffect(() => {
		if (!driftClientIsReady || !currentAccount?.client) return;

		try {
			const beforeLiqPriceNum = getSpotLiqPriceNum(
				currentAccount?.client,
				selectedMarket?.market.marketIndex ?? 0
			);
			const beforeLiqPriceStr = getSpotLiqPriceStr(
				currentAccount?.client,
				selectedMarket?.market.marketIndex ?? 0
			);

			const afterLiqPriceNum = getSpotLiqPriceNum(
				currentAccount?.client,
				selectedMarket?.market.marketIndex ?? 0,
				baseSizeBigNum,
				side === 'buy'
			);
			const afterLiqPriceStr = getSpotLiqPriceStr(
				currentAccount?.client,
				selectedMarket?.market.marketIndex ?? 0,
				baseSizeBigNum,
				side === 'buy'
			);

			setBeforeLiqPriceNum(beforeLiqPriceNum);
			setBeforeLiqPriceStr(beforeLiqPriceStr);
			setAfterLiqPriceNum(afterLiqPriceNum);
			setAfterLiqPriceStr(afterLiqPriceStr);
		} catch (err) {
			// swallow error, will throw for 0 base size
			//console.error(err);
		}
	}, [selectedMarket, markPrice, baseSizeStringValue, driftClientIsReady]);

	const isMobileScreenSize = useIsMobileScreenSize();

	if (!selectedMarket || !currentAccount || !driftClientIsReady) {
		return null;
	}

	return (
		<div className="flex w-full flex-col gap-y-sm">
			{orderType === 'oracleLimit' && <LimitOraclePriceDisplay />}

			{showSlippageTolerance &&
				(isMobileScreenSize ? (
					<BottomDrawerSlippageToleranceInput />
				) : (
					<CollapsibleSlippageToleranceInput />
				))}

			{!isMobileScreenSize && (
				<>
					<ValueDisplay.Default
						label={`Order ${sizeInputSource === 'base' ? 'Value' : 'Size'}`}
						value={
							sizeInputSource === 'base'
								? toNotional(quoteSize)
								: `${trimTrailingZeros(
										baseSizeBigNum.prettyPrint(
											false,
											undefined,
											stepSizeDecimalPlaces
										)
									)} ${baseAssetSymbol}`
						}
					/>

					<ValueDisplay.ValueChange
						label="Position"
						previousValue={previousBaseValue}
						afterValue={afterBaseValue}
						previousValuePrint={`${previousBaseValue
							.toNum()
							.toLocaleString('en-US', {
								maximumSignificantDigits: 5,
							})} ${baseAssetSymbol}`}
						afterValuePrint={`${roundToStepSizeIfLargeEnough(
							afterBaseValue.toNum().toLocaleString('en-US', {
								maximumSignificantDigits: 5,
							}),
							stepSize
						)} ${baseAssetSymbol}`}
						forceWhite
					/>
				</>
			)}

			<LiquidationPriceDisplay
				orderType={orderType}
				liqPriceAfter={afterLiqPriceNum}
				liqPriceAfterString={afterLiqPriceStr}
				liqPriceBefore={beforeLiqPriceNum}
				liqPriceBeforeString={beforeLiqPriceStr}
				userAccountIsReady={userAccountIsReady}
				selectedMarketIndex={selectedMarket?.market.marketIndex}
				baseSize={baseSizeBigNum}
				isMobileScreenSize={isMobileScreenSize}
			/>

			{!isPostOnly &&
				['market', 'limit', 'stopMarket', 'stopLimit', 'oracleLimit'].includes(
					orderType
				) && (
					<ValueDisplay.Default
						label="Fees"
						value={`${
							orderType === 'oracleLimit' ? '~' : ''
						}$${tradeFee.toFixed(2)}`}
					/>
				)}
		</div>
	);
};

export default React.memo(SpotTradeDetails);
